// Ficha del deal: panel lateral en escritorio, pantalla completa con «regresar»
// en el celular. Etapa, datos editables, seguimiento (semáforo + bitácora de
// contactos) — todo con endpoints existentes de apps/deals.

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, History, Loader2, PhoneCall, Save, X } from 'lucide-react';

import { dealsApi } from '../api';
import {
  addDays, CHANNEL_LABEL, fmtDay, fmtMoney, fmtWhen, OUTCOME_LABEL, todayCivil,
} from '../model';
import type { ContactChannel, ContactRow, Deal, DealStage, FollowUpRow } from '../types';
import { FollowUpChip, OwnerAvatar, ownerText, StageChip, stageStyle } from './parts';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;

export function DealSheet({
  deal: initial, stages, followUp, isMobile, isSeller, onClose, onChanged, onMove, notify,
}: {
  deal: Deal;
  stages: DealStage[];
  followUp?: FollowUpRow;
  isMobile: boolean;
  isSeller: boolean;
  onClose: () => void;
  onChanged: (d: Deal) => void;
  onMove: (d: Deal, s: DealStage) => void;
  notify: Notify;
}) {
  const navigate = useNavigate();
  const [deal, setDeal] = useState<Deal>(initial);
  const [contacts, setContacts] = useState<ContactRow[] | null>(null);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const titleRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => { setDeal(initial); }, [initial]);

  // Detalle fresco + bitácora al abrir.
  useEffect(() => {
    let alive = true;
    setContacts(null);
    setContactsError(null);
    setEditing(false);
    titleRef.current?.focus();
    dealsApi.get(initial.id).then((d) => { if (alive) setDeal(d); }).catch(() => { /* se queda lo de la lista */ });
    dealsApi.listContacts(initial.id)
      .then((c) => { if (alive) setContacts(c); })
      .catch((e: any) => { if (alive) setContactsError(e?.message || 'No se pudo cargar la bitácora'); });
    return () => { alive = false; };
  }, [initial.id]);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-dl-modal]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const stage = stages.find((s) => s.id === deal.stage_id);
  const open = deal.status === 'open';

  const onStageKey = (e: KeyboardEvent) => {
    const i = Math.max(0, stages.findIndex((s) => s.id === deal.stage_id));
    const n = stages.length;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); onMove(deal, stages[(i + 1) % n]); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); onMove(deal, stages[(i - 1 + n) % n]); }
  };

  return (
    <aside aria-label={`Ficha de ${deal.title}`}
      className={`dl-sheet-in flex h-full min-h-0 flex-col bg-card ${isMobile ? '' : 'w-[420px] shrink-0 border-l border-border xl:w-[460px]'}`}>
      <header className="flex items-start gap-3 border-b border-border px-4 py-3">
        {isMobile && (
          <button type="button" onClick={onClose} aria-label="Regresar al pipeline"
            className="-ml-1 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-foreground hover:bg-muted">
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h2 ref={titleRef} tabIndex={-1} className="text-[16px] font-semibold leading-snug text-foreground">{deal.title}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <StageChip stage={stage} name={deal.stage_name} />
            {followUp && open && <FollowUpChip row={followUp} />}
          </div>
        </div>
        {!isMobile && (
          <button type="button" onClick={onClose} aria-label="Cerrar ficha"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground">
            <X className="h-5 w-5" aria-hidden />
          </button>
        )}
      </header>

      <div className="dl-scroll min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        {/* Montos (del servidor) */}
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Valor" value={fmtMoney(deal.value_amount, deal.currency)} />
          <Stat label={`Ponderado · ${deal.probability}%`} value={fmtMoney(deal.weighted_value, deal.currency)} />
        </div>

        {/* Etapa */}
        <Section title="Etapa">
          <div role="radiogroup" aria-label="Etapa del deal" onKeyDown={onStageKey}
            className="grid grid-cols-2 gap-1 rounded-xl border border-border bg-muted p-1 min-[420px]:grid-cols-3">
            {stages.map((s) => {
              const sel = s.id === deal.stage_id;
              return (
                <button key={s.id} type="button" role="radio" aria-checked={sel} tabIndex={sel ? 0 : -1} style={stageStyle(s)}
                  onClick={() => !sel && onMove(deal, s)}
                  className={`flex min-h-[40px] min-w-0 items-center gap-1.5 rounded-[10px] px-2 py-1.5 text-[12.5px] font-medium transition-colors ${sel ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  <span className={`dl-dot h-2 w-2 shrink-0 rounded-full ${sel ? '' : 'opacity-70'}`} aria-hidden />
                  <span className="truncate">{s.name}</span>
                </button>
              );
            })}
          </div>
          {deal.status === 'lost' && deal.lost_reason && (
            <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-[12.5px] text-foreground"><span className="text-muted-foreground">Motivo de pérdida: </span>{deal.lost_reason}</p>
          )}
        </Section>

        {/* Datos */}
        <Section title="Datos" action={!editing && (
          <button type="button" onClick={() => setEditing(true)} className="rounded-lg px-2 py-1 text-[12.5px] font-medium dl-accent-text hover:bg-muted">Editar</button>
        )}>
          {editing ? (
            <EditForm deal={deal} onCancel={() => setEditing(false)} onSaved={(d) => { setDeal(d); onChanged(d); setEditing(false); notify('success', 'Deal actualizado'); }} notify={notify} />
          ) : (
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-[13px]">
              <Row k="Cliente">
                {deal.client_name || followUp?.client_name || (deal.lead_id ? 'Lead ligado' : <span className="text-muted-foreground">Sin cliente ligado</span>)}
                {deal.lead_id && (
                  <button type="button" onClick={() => navigate(`/m/crm/leads?lead=${encodeURIComponent(deal.lead_id!)}`)}
                    className="ml-2 inline-flex items-center gap-1 text-[12.5px] font-medium dl-accent-text hover:underline">
                    Ver lead<ExternalLink className="h-3 w-3" aria-hidden />
                  </button>
                )}
              </Row>
              <Row k="Responsable">
                <span className="inline-flex items-center gap-2">
                  <OwnerAvatar ownerId={deal.owner_user_id} name={deal.owner_name} mine={isSeller} size={22} />
                  {ownerText(deal.owner_user_id, deal.owner_name, isSeller) ?? <span className="text-muted-foreground">Sin responsable</span>}
                </span>
              </Row>
              <Row k="Cierre esperado">{deal.expected_close_date ? fmtDay(deal.expected_close_date, true) : <span className="text-muted-foreground">Sin fecha</span>}</Row>
              {deal.closed_at && <Row k="Cerrado">{fmtWhen(deal.closed_at)}</Row>}
              <Row k="Creado">{fmtWhen(deal.created_at)}</Row>
              <Row k="Actualizado">{fmtWhen(deal.updated_at)}</Row>
              {deal.description && <Row k="Notas"><span className="whitespace-pre-wrap">{deal.description}</span></Row>}
            </dl>
          )}
        </Section>

        {/* Seguimiento */}
        <Section title="Seguimiento" icon={<History className="h-4 w-4" aria-hidden />}>
          {open ? (
            <ContactForm deal={deal} onDone={(c) => {
              setContacts((cs) => [c, ...(cs || [])]);
              notify('success', c.outcome === 'reagendar' ? `Próximo contacto: ${fmtDay(c.rescheduled_to)}` : 'Contacto registrado');
              dealsApi.get(deal.id).then((d) => { setDeal(d); onChanged(d); }).catch(() => {});
            }} notify={notify} />
          ) : (
            <p className="text-[12.5px] text-muted-foreground">El deal está cerrado; ya no lleva seguimiento. Para reabrirlo, muévelo a una etapa abierta.</p>
          )}
          <div className="mt-3">
            {contactsError ? (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive">{contactsError}</p>
            ) : contacts === null ? (
              <p className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground" role="status"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Cargando bitácora…</p>
            ) : contacts.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border px-3 py-4 text-center text-[12.5px] text-muted-foreground">Aún no hay contactos registrados.</p>
            ) : (
              <ol className="space-y-2">
                {contacts.map((c) => (
                  <li key={c.id} className="rounded-xl border border-border bg-background/60 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-x-2 text-[12.5px]">
                      <span className="font-semibold text-foreground">{OUTCOME_LABEL[c.outcome] || c.outcome}</span>
                      <span className="text-muted-foreground">· {CHANNEL_LABEL[c.channel] || c.channel} · {fmtDay(c.contacted_on)}</span>
                      {c.rescheduled_to && <span className="text-muted-foreground">→ {fmtDay(c.rescheduled_to)}</span>}
                    </div>
                    {c.note && <p className="mt-1 whitespace-pre-wrap text-[12.5px] text-foreground/90">{c.note}</p>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Section>
      </div>
    </aside>
  );
}

function Section({ title, icon, action, children }: { title: string; icon?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        {icon && <span className="dl-accent-text">{icon}</span>}
        <h3 className="flex-1 text-[13px] font-semibold text-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 px-3 py-2.5">
      <div className="text-[11.5px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-[17px] font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function Row({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="min-w-0 text-foreground">{children}</dd>
    </>
  );
}

function EditForm({ deal, onCancel, onSaved, notify }: { deal: Deal; onCancel: () => void; onSaved: (d: Deal) => void; notify: Notify }) {
  const [f, setF] = useState({
    title: deal.title, value_amount: String(deal.value_amount ?? ''), probability: String(deal.probability),
    expected_close_date: deal.expected_close_date || '', description: deal.description || '',
  });
  const [busy, setBusy] = useState(false);
  const prob = Number(f.probability);
  const valid = f.title.trim() && !Number.isNaN(Number(f.value_amount)) && Number(f.value_amount) >= 0 && prob >= 0 && prob <= 100;
  const save = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const d = await dealsApi.update(deal.id, {
        title: f.title.trim(), value_amount: f.value_amount || '0', probability: Math.round(prob),
        expected_close_date: f.expected_close_date || null, description: f.description,
      });
      onSaved(d);
    } catch (e: any) { notify('error', 'No se guardó', e?.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <Field label="Título"><input className="dl-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`Valor (${deal.currency})`}><input className="dl-input" type="number" inputMode="decimal" min={0} step="0.01" value={f.value_amount} onChange={(e) => setF({ ...f, value_amount: e.target.value })} /></Field>
        <Field label="Probabilidad %"><input className="dl-input" type="number" inputMode="numeric" min={0} max={100} value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} /></Field>
      </div>
      <Field label="Cierre esperado"><input className="dl-input" type="date" value={f.expected_close_date} onChange={(e) => setF({ ...f, expected_close_date: e.target.value })} /></Field>
      <Field label="Notas"><textarea className="dl-input" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-10 rounded-xl px-3.5 text-[13.5px] font-medium text-foreground hover:bg-muted">Cancelar</button>
        <button type="button" onClick={save} disabled={!valid || busy} className="dl-btn-brand inline-flex h-10 items-center gap-1.5 rounded-xl px-4 text-[13.5px] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}Guardar
        </button>
      </div>
    </div>
  );
}

/** Registrar contacto (POST /{id}/contact). Ganar/perder se hace moviendo la
 *  etapa, para que etapa y estado del deal no se desalineen. */
function ContactForm({ deal, onDone, notify }: { deal: Deal; onDone: (c: ContactRow) => void; notify: Notify }) {
  const today = todayCivil();
  const [outcome, setOutcome] = useState<'reagendar' | 'no_contesto'>('reagendar');
  const [channel, setChannel] = useState<ContactChannel>('llamada');
  const [date, setDate] = useState(addDays(today, 2));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const valid = outcome === 'no_contesto' || (date && date > today);

  if (!openForm) {
    return (
      <button type="button" onClick={() => setOpenForm(true)}
        className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted">
        <PhoneCall className="h-4 w-4" aria-hidden />Registrar contacto
      </button>
    );
  }
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const c = await dealsApi.registerContact(deal.id, {
        outcome, channel, note: note.trim(), contacted_on: today, rescheduled_to: outcome === 'reagendar' ? date : null,
      });
      onDone(c);
      setNote('');
      setOpenForm(false);
    } catch (e: any) { notify('error', 'No se registró el contacto', e?.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-border bg-background/60 p-3">
      <div role="radiogroup" aria-label="¿Cómo terminó el contacto?" className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1">
        {([['reagendar', 'Habló · reagendar'], ['no_contesto', 'No contestó']] as const).map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={outcome === k} onClick={() => setOutcome(k)}
            className={`min-h-[40px] rounded-[10px] px-2 text-[12.5px] font-medium ${outcome === k ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Canal">
          <select className="dl-input" value={channel} onChange={(e) => setChannel(e.target.value as ContactChannel)}>
            {Object.entries(CHANNEL_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        {outcome === 'reagendar' && (
          <Field label="Próximo contacto"><input className="dl-input" type="date" min={addDays(today, 1)} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        )}
      </div>
      <Field label="Nota"><textarea className="dl-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Qué se habló, para no repetir la plática" /></Field>
      {outcome === 'reagendar' && date && date <= today && <p className="text-[12px] text-destructive">El próximo contacto debe ser después de hoy.</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOpenForm(false)} className="h-10 rounded-xl px-3.5 text-[13.5px] font-medium text-foreground hover:bg-muted">Cancelar</button>
        <button type="button" onClick={submit} disabled={!valid || busy} className="dl-btn-brand inline-flex h-10 items-center gap-1.5 rounded-xl px-4 text-[13.5px] font-semibold">
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}Registrar
        </button>
      </div>
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[12.5px] font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11.5px] text-muted-foreground">{hint}</span>}
    </label>
  );
}
